import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../services/api';
import { toast } from 'sonner';

export interface DashboardInsight {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  recommendation: string;
  actionType: 'bounce_rate' | 'mobile_optimization' | 'seo_strategy' | 'conversion_optimization';
  impact: string;
  businessValue: string;
  dataSource: {
    metric: string;
    currentValue: number;
    benchmarkValue: number;
    difference: number;
    period: string;
  };
}

export interface DashboardInsightsResponse {
  insights: DashboardInsight[];
  lastUpdated: string;
  cached: boolean;
}

export function useDashboardInsights(projectId?: string) {
  const queryClient = useQueryClient();

  // Get dashboard insights
  const {
    data: insightsData,
    isLoading,
    error,
    refetch
  } = useQuery<DashboardInsightsResponse>({
    queryKey: ['dashboard-insights', projectId],
    queryFn: () => analyticsApi.getDashboardInsights(projectId!),
    enabled: !!projectId,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    retry: (failureCount, error: any) => {
      // Don't retry if no GA4 connection
      if (error?.response?.status === 400) {
        return false;
      }
      return failureCount < 2;
    }
  });

  // Force refresh insights (ignores cache)
  const refreshInsightsMutation = useMutation({
    mutationFn: async () => {
      // Invalidate cache first to force fresh fetch
      queryClient.invalidateQueries({ queryKey: ['dashboard-insights', projectId] });
      return analyticsApi.getDashboardInsights(projectId!);
    },
    onSuccess: (data) => {
      // Update the cache with fresh data
      queryClient.setQueryData(['dashboard-insights', projectId], data);
      toast.success('Dashboard insights refreshed');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to refresh insights');
    }
  });

  // Helper to get insights by severity
  const getInsightsBySeverity = (severity: string) => {
    return insightsData?.insights?.filter(insight => insight.severity === severity) || [];
  };

  // Helper to check if insights are stale (older than 6 days)
  const areInsightsStale = () => {
    if (!insightsData?.lastUpdated) return false;
    const lastUpdate = new Date(insightsData.lastUpdated);
    const sixDaysAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);
    return lastUpdate < sixDaysAgo;
  };

  // Helper to format last updated time
  const getLastUpdatedText = () => {
    if (!insightsData?.lastUpdated) return 'Never';
    
    const lastUpdate = new Date(insightsData.lastUpdated);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  };

  return {
    insights: insightsData?.insights || [],
    lastUpdated: insightsData?.lastUpdated,
    cached: insightsData?.cached || false,
    isLoading,
    error,
    refetch,
    refreshInsights: refreshInsightsMutation.mutateAsync,
    isRefreshing: refreshInsightsMutation.isPending,
    getInsightsBySeverity,
    areInsightsStale,
    getLastUpdatedText,
    // Helper getters for common insight types
    criticalInsights: getInsightsBySeverity('critical'),
    highInsights: getInsightsBySeverity('high'),
    mediumInsights: getInsightsBySeverity('medium'),
    lowInsights: getInsightsBySeverity('low')
  };
} 